import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { OnChangeFn, Row, SortingState } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DataTable } from '@/components/ui/table/data-table';
import useBrains from '@/hooks/useBrains';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Campaign } from '@/shared/types/campaigns';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import { columns } from './columns';
import { CampaignsSelectionActions } from './SelectionActions';
import { getCampaignDetailPage } from '../../../util/getCampaignDetailPage';
import { BulkDeleteCampaignsModal } from '../../modals/BulkDeleteCampaignsModal';

interface CampaignsTableProps {
  data: Campaign[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  currentPageIndex: number;
  setCurrentPageIndex: (index: number) => void;
  rowCount: number;
  onDeleteClick: (campaignId: string) => void;
  onStopClick: (campaignId: string) => void;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  /** Changes whenever a filter, the search term or the sort changes, to reset the selection. */
  selectionResetKey?: string;
  renderToolbar?: (selectionActions: ReactNode) => ReactNode;
}

const renderSelectionActionsOnly = (selectionActions: ReactNode) => selectionActions;

export const CampaignsTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  rowCount,
  onDeleteClick,
  onStopClick,
  isLoading,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  selectionResetKey,
  renderToolbar = renderSelectionActionsOnly,
}: CampaignsTableProps) => {
  const { t } = useTranslation();
  const { brains } = useBrains();
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'campaigns', actions.WRITE)
  );

  const [bulkDeletePending, setBulkDeletePending] = useState<Campaign[] | null>(null);
  // Programmatic focus target of last resort after the bulk-delete dialog closes; `tabIndex={-1}`
  // keeps it focusable without adding a tab stop.
  const tableRef = useRef<HTMLDivElement>(null);

  const tableColumns = useMemo(
    () => columns({ brains, onDeleteClick, onStopClick }),
    [brains, onDeleteClick, onStopClick]
  );

  // The server rejects DELETE for a running campaign — those rows keep the row-menu
  // cancel-then-delete flow as their only path.
  const enableRowSelection = useMemo(
    () => (canWrite ? (row: Row<Campaign>) => row.original.status !== 'in_progress' : false),
    [canWrite]
  );

  const getRowHref = useCallback(
    (campaign: Campaign) => {
      const page = getCampaignDetailPage(campaign.status);
      return `/${slug}/campaigns/${campaign.campaign_id}/${page}`;
    },
    [slug]
  );

  // The selection-actions trash button that opened the modal unmounts when the selection resets,
  // so Radix's default focus restore lands on <body>. Hand focus to the select-all checkbox —
  // or, when the delete leaves nothing selectable on the page (the header checkbox renders only
  // while at least one row can be selected), to the table itself, which always survives.
  const handleModalCloseAutoFocus = useCallback((event: Event) => {
    const target =
      document.querySelector<HTMLElement>('[data-table-select-all]') ?? tableRef.current;
    if (!target) {
      return;
    }
    event.preventDefault();
    target.focus();
  }, []);

  // Deleting every row of a page would leave the user staring at an empty page until the
  // reconciling refetch repacks the list — step back instead. Compare ids, not lengths: rows
  // can be swapped in/out by RTM events between opening the modal and the delete landing.
  const handleBulkDeleted = useCallback(() => {
    const pendingIds = new Set(bulkDeletePending?.map((campaign) => campaign.campaign_id));
    const wholePageDeleted =
      data.length > 0 && data.every((campaign) => pendingIds.has(campaign.campaign_id));
    if (wholePageDeleted && currentPageIndex > 0) {
      setCurrentPageIndex(currentPageIndex - 1);
    }
  }, [bulkDeletePending, data, currentPageIndex, setCurrentPageIndex]);

  return (
    <>
      <div ref={tableRef} tabIndex={-1} className="flex min-h-0 flex-1 flex-col outline-none">
        <DataTable.Root<Campaign, unknown>
          fill
          className="min-h-0 flex-1"
          columns={tableColumns}
          data={data}
          pagination
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
          rowCount={rowCount}
          resourceName="campaign"
          emptyStateMessage={t('campaigns.no_campaigns_found')}
          isFetchingNextPage={isFetchingNextPage}
          isLoading={isLoading}
          sorting={sorting}
          onSortingChange={onSortingChange}
          manualSorting
          enableRowHover
          getRowHref={getRowHref}
          getRowId={(row) => row.campaign_id}
          enableRowSelection={enableRowSelection}
        >
          <DataTable.Toolbar<Campaign>>
            {(table) =>
              renderToolbar(
                <CampaignsSelectionActions
                  table={table}
                  data={data}
                  selectionResetKey={selectionResetKey}
                  currentPageIndex={currentPageIndex}
                  onDelete={setBulkDeletePending}
                />
              )
            }
          </DataTable.Toolbar>
          <DataTable.Content />
          <DataTable.Pagination />
        </DataTable.Root>
      </div>

      <BulkDeleteCampaignsModal
        campaigns={bulkDeletePending}
        onClose={() => setBulkDeletePending(null)}
        onDeleted={handleBulkDeleted}
        onCloseAutoFocus={handleModalCloseAutoFocus}
      />
    </>
  );
};
